import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconBack, IconEdit, IconTrash } from '../components/icons';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { formatDateShort } from '../utils/format';

// TS port of the source (Falah) app's pages/EventDetail.jsx — ported as-is.
interface EventRecord {
  id: number;
  title: string;
  description: string | null;
  start_at: string;
  end_at: string | null;
  is_public_holiday: boolean;
}

export function EventDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [event, setEvent] = useState<EventRecord | null>(null);

  useEffect(() => {
    api.get<EventRecord>(`/events/${id}`).then((res) => setEvent(res.data));
  }, [id]);

  const remove = async () => {
    if (!event) return;
    if (!(await confirmDelete(`Delete "${event.title}"?`))) return;
    try {
      await api.delete(`/events/${id}`);
      navigate('/events');
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  if (!event) return null;

  return (
    <>
      <header className="page-header">
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/events')} />
          <IconButton icon={IconEdit} label="Edit" to={`/events/${id}/edit`} />
          <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={remove} />
        </div>
      </header>

      <div className="card">
        <div className="detail-summary">
          <div>
            <div className="detail-summary-title">{event.title}</div>
            {event.is_public_holiday && <span className="badge badge-active">Public Holiday</span>}
          </div>
        </div>

        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-item-label">Starts</span>
            <span className="detail-item-value">{formatDateShort(event.start_at)}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Ends</span>
            <span className={`detail-item-value${event.end_at ? '' : ' empty'}`}>
              {event.end_at ? formatDateShort(event.end_at) : 'Not set'}
            </span>
          </div>
        </div>

        <div className="detail-item detail-item-full">
          <span className="detail-item-label">Description</span>
          {event.description ? (
            <p className="detail-notes">{event.description}</p>
          ) : (
            <span className="detail-item-value empty">Not provided</span>
          )}
        </div>
      </div>
    </>
  );
}
